import type {
  ClientToServerMessage,
  ServerToClientMessage,
  SubscriptionChannel,
} from './types';
import { useConnectionStore } from '../stores/connectionStore';
import { useIntegrityStore } from '../stores/integrityStore';
import { useAuthStore } from '../stores/authStore';

type MessageListener = (msg: ServerToClientMessage) => void;

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

let idCounter = 0;
function nextRequestId(): string {
  return `req-${++idCounter}-${Date.now()}`;
}

export class GatewayClient {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private intentionalClose = false;
  private socketGeneration = 0;
  private readonly listeners = new Set<MessageListener>();

  /** Pending command replies: requestId → resolve/reject */
  private readonly pendingReplies = new Map<
    string,
    { resolve: (payload: unknown) => void; reject: (err: Error) => void }
  >();

  constructor(url: string) {
    this.url = url;
  }

  connect(): void {
    this.intentionalClose = false;
    // Close any existing socket before creating a new one
    if (this.ws) {
      const old = this.ws;
      this.ws = null;
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      old.close();
    }
    this.createSocket();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      const old = this.ws;
      this.ws = null;
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      old.close();
    }
    useConnectionStore.getState().setStatus('disconnected');
    // Drop cached service-health snapshots so HealthSummary doesn't keep
    // reporting data-integrity as online from stale data after the WS dies.
    useIntegrityStore.getState().clearHealth();
  }

  /** Subscribe to a data channel. Returns an unsubscribe function. */
  subscribe(
    channel: SubscriptionChannel,
    symbol?: string,
    interval?: number,
    periods?: number[],
    opts?: { portfolioId?: string; token?: string },
  ): () => void {
    const requestId = nextRequestId();
    const subMsg: ClientToServerMessage = {
      type: 'subscribe',
      requestId,
      channel,
      ...(symbol != null ? { symbol } : {}),
      ...(interval != null ? { interval } : {}),
      ...(periods != null ? { periods } : {}),
      ...(opts?.portfolioId != null ? { portfolioId: opts.portfolioId } : {}),
      ...(opts?.token != null ? { token: opts.token } : {}),
    };
    this.send(subMsg);

    return () => {
      const unsubMsg: ClientToServerMessage = {
        type: 'unsubscribe',
        requestId: nextRequestId(),
        channel,
        ...(symbol != null ? { symbol } : {}),
        ...(opts?.portfolioId != null ? { portfolioId: opts.portfolioId } : {}),
      };
      this.send(unsubMsg);
    };
  }

  /** Send a command and return a promise that resolves with the reply. */
  command(
    target: 'market-data' | 'data-store' | 'signal-engine' | 'data-integrity' | 'market-sentiment' | 'strategy-engine',
    payload: { type: string } & Record<string, unknown>,
    timeoutMs = 10_000,
  ): Promise<unknown> {
    const requestId = nextRequestId();

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingReplies.delete(requestId);
        reject(new Error(`Command timeout: ${payload.type}`));
      }, timeoutMs);

      this.pendingReplies.set(requestId, {
        resolve: (val) => {
          clearTimeout(timer);
          resolve(val);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      });

      // Attach the auth token: the gateway's command handler verifies msg.token
      // (JWT) before forwarding admin commands (start-symbol, force-backfill, …).
      // Without it the gateway rejects with "Commands require authentication".
      const token = useAuthStore.getState().accessToken;
      this.send({
        type: 'command',
        requestId,
        target,
        payload,
        ...(token ? { token } : {}),
      });
    });
  }

  /** Request a state snapshot from the server. */
  requestSnapshot(kind: 'symbols' | 'active' | 'ticker' | 'metrics' | 'full'): void {
    this.send({
      type: 'request-snapshot',
      requestId: nextRequestId(),
      kind,
    });
  }

  /** Add a listener for all incoming messages. */
  addListener(fn: MessageListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private send(msg: ClientToServerMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private createSocket(): void {
    useConnectionStore.getState().setStatus('connecting');

    // Dev-only churn: React StrictMode double-invokes effects, so the first
    // WebSocket is created and immediately closed by the cleanup. Chrome logs
    // "WebSocket is closed before the connection is established" — expected.
    const generation = ++this.socketGeneration;
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.onopen = () => {
      if (generation !== this.socketGeneration) return;
      this.reconnectAttempt = 0;
      useConnectionStore.getState().setStatus('connected');
      this.startPing();
    };

    ws.onmessage = (event) => {
      if (generation !== this.socketGeneration) return;
      try {
        const msg = JSON.parse(event.data as string) as ServerToClientMessage;
        this.handleMessage(msg);
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      if (generation !== this.socketGeneration) return;
      this.stopPing();
      if (!this.intentionalClose) {
        useConnectionStore.getState().setStatus('reconnecting');
        // Same reasoning as `disconnect()`: a closed socket means we cannot
        // trust any cached service-health snapshot, even though the user is
        // still on the page. The HealthSummary freshness selector also catches
        // this, but clearing eagerly avoids a 90s stale-but-fresh window.
        useIntegrityStore.getState().clearHealth();
        this.scheduleReconnect();
      }
    };

    ws.onerror = () => {
      // onclose will fire after this
    };
  }

  private handleMessage(msg: ServerToClientMessage): void {
    // Handle internal protocol messages
    switch (msg.type) {
      case 'connected':
        useConnectionStore.getState().setClientId(msg.clientId);
        break;

      case 'pong': {
        const latency = Date.now() - msg.ts;
        useConnectionStore.getState().setLatency(latency);
        break;
      }

      case 'command-reply': {
        const pending = this.pendingReplies.get(msg.requestId);
        if (pending) {
          this.pendingReplies.delete(msg.requestId);
          pending.resolve(msg.payload);
        }
        break;
      }

      case 'error': {
        if (msg.requestId) {
          const pending = this.pendingReplies.get(msg.requestId);
          if (pending) {
            this.pendingReplies.delete(msg.requestId);
            pending.reject(new Error(msg.message));
          }
        }
        break;
      }
    }

    // Notify all listeners
    for (const fn of this.listeners) {
      try {
        fn(msg);
      } catch {
        // Don't let a bad listener break the loop
      }
    }
  }

  private scheduleReconnect(): void {
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** this.reconnectAttempt,
      RECONNECT_MAX_MS,
    );
    this.reconnectAttempt++;
    useConnectionStore.getState().incrementReconnect();

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.createSocket();
    }, delay);
  }

  private startPing(): void {
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping', ts: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
